import { FilterPrefix, FilterToken } from '../types/selectiveEditTypes';

const KNOWN_PREFIXES: FilterPrefix[] = ['node', 'step', 'type', 'speaker'];
const PREFIX_PATTERN = /\b(node|step|type|speaker):/gi;

let nextTokenId = 1;

function makeId(): string {
  return `ft-${nextTokenId++}`;
}

/**
 * Parse a raw input string into filter tokens.
 *
 * Syntax:
 *   "node:ghost"              → [{ prefix: 'node', value: 'ghost' }]
 *   "node:ghost step:charm"   → [{ prefix: 'node', value: 'ghost' }, { prefix: 'step', value: 'charm' }]
 *   "ghost"                   → [{ prefix: 'general', value: 'ghost' }]
 *   "node:ghost charm"        → [{ prefix: 'node', value: 'ghost charm' }]
 *
 * Rules:
 *   - Known prefixes: node, step, type
 *   - Value extends until the next known prefix or end of string
 *   - If no prefix is detected, the entire input is a single 'general' token
 *   - Empty values after a prefix are skipped
 */
export function parseFilterTokens(input: string): FilterToken[] {
  const trimmed = input.trim();
  if (!trimmed) return [];

  // Find all prefix positions
  const prefixMatches: Array<{ prefix: FilterPrefix; index: number; matchLength: number }> = [];
  PREFIX_PATTERN.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = PREFIX_PATTERN.exec(trimmed)) !== null) {
    prefixMatches.push({
      prefix: match[1].toLowerCase() as FilterPrefix,
      index: match.index,
      matchLength: match[0].length,
    });
  }

  // No prefixes found — entire input is a general token
  if (prefixMatches.length === 0) {
    return [{ id: makeId(), prefix: 'general', value: trimmed, raw: trimmed }];
  }

  const tokens: FilterToken[] = [];

  // Text before the first prefix → general token
  if (prefixMatches[0].index > 0) {
    const beforeText = trimmed.slice(0, prefixMatches[0].index).trim();
    if (beforeText) {
      tokens.push({ id: makeId(), prefix: 'general', value: beforeText, raw: beforeText });
    }
  }

  // Process each prefix match
  for (let i = 0; i < prefixMatches.length; i++) {
    const pm = prefixMatches[i];
    const valueStart = pm.index + pm.matchLength;
    const valueEnd = i + 1 < prefixMatches.length ? prefixMatches[i + 1].index : trimmed.length;
    const value = trimmed.slice(valueStart, valueEnd).trim();

    if (value) {
      const raw = trimmed.slice(pm.index, valueEnd).trim();
      tokens.push({ id: makeId(), prefix: pm.prefix, value, raw });
    }
  }

  return tokens;
}

/**
 * Serialize tokens back to a display string (for history display, etc.).
 */
export function tokensToDisplayString(tokens: FilterToken[]): string {
  return tokens.map((t) => (t.prefix === 'general' ? t.value : `${t.prefix}:${t.value}`)).join(' ');
}

/**
 * Check if the current input text starts with a partial prefix match.
 * Returns matching prefix suggestions for autocomplete.
 */
export function getMatchingPrefixes(input: string): FilterPrefix[] {
  const trimmed = input.trim().toLowerCase();
  if (!trimmed || trimmed.includes(':')) return [];
  return KNOWN_PREFIXES.filter((p) => p.startsWith(trimmed));
}
